/**
 * This Header is used as the "built in" header for all adaptive cards.  It is still driven by parts
 * of the theme file (for color changes mostly) and is activated by the template having a "header" element.
 */

import { formatDistanceToNow, isSameMonth, isSameYear } from 'date-fns'
import i18next from 'i18next'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import ImageWithFallback from '@/utilities/client-components/components/Icon/ImageWithFallback'
import dateFnsLocales from '@/utilities/client-components/translations/dateFnsLocales'
import * as themeUtilities from '@/utilities/client-components/utilities/themeUtils'

const StyledHeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 16px;
  flex: 1;
`

const StyledImage = styled(ImageWithFallback)`
  height: 40px;
  width: 100%;
  max-width: 40px;
  object-fit: cover;
`

const StyledTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 12px;
  flex: 1;
`

const StyledTitle = styled.p`
  font-weight: 600;
  font-size: 14px;
  margin: 0;
  color: ${(props: any) => themeUtilities.getRgbaColor(props.theme, 'dark')};
`

const StyledSentTime = styled.p`
  font-size: 12px;
  margin: 0;
  color: ${(props: any) => themeUtilities.getRgbaColor(props.theme, 'light')};
`

const getSourceTimestamp = (received: any, t: any) => {
  const receivedDate = new Date(received)
  if (isSameMonth(receivedDate, new Date())) {
    // @ts-expect-error
    return formatDistanceToNow(receivedDate, { includeSeconds: true, locale: dateFnsLocales[i18next.languages[0]] })
  } else if (isSameYear(receivedDate, new Date())) {
    return t('client:Header.receivedThisMonth', { date: receivedDate })
  } else {
    return t('client:Header.received', { date: receivedDate })
  }
}

interface HeaderProps {
  source: {
    name: string
    iconUrl: string
    iconAlt?: string
    received: string
  }
}

const Header = ({ source }: HeaderProps) => {
  const { t } = useTranslation('client')

  return (
    <StyledHeaderContainer data-item="wg-header">
      <StyledImage src={source.iconUrl} alt={source.iconAlt} />
      <StyledTitleContainer>
        <StyledTitle>{source.name}</StyledTitle>
        <StyledSentTime>{getSourceTimestamp(source.received, t)}</StyledSentTime>
      </StyledTitleContainer>
    </StyledHeaderContainer>
  )
}

Header.displayName = 'Header'
Header.defaultProps = {
  source: {}
}

export default Header
